import { useNavigate, Link } from "react-router-dom";
import { useState } from "react";
import { supabase } from "../config/supabaseClient";
import { Form, Input, Button, Typography, Divider, Card, App } from "antd";
import { MailOutlined, LockOutlined, LoginOutlined } from "@ant-design/icons";
import loginImage from "../assets/Homepage.jpeg";

const { Title, Text } = Typography;

const ROLE_LABEL = { student: "Student", teacher: "Teacher", admin: "Admin" };

const LOGIN_REDIRECT = {
  admin: "/dashboard",
  teacher: "/teacher-dashboard",
  student: "/student-dashboard",
};

export default function LoginForm({ role }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const roleLabel = ROLE_LABEL[role];
  const { message } = App.useApp();

  const handleLogin = async (values) => {
    const { email, password } = values;
    setLoading(true);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        message.error(error.message);
        return;
      }

      const { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select("type")
        .eq("id", data.user.id)
        .single();

      let finalProfile = profileData;
      let finalError = profileError;

      if (finalError && (finalError.code === "PGRST116" || finalError.status === 406)) {
        const meta = data.user.user_metadata || {};
        const desiredType = ["student", "teacher"].includes(meta.type)
          ? meta.type
          : role;
        const { error: upsertError } = await supabase
          .from("profiles")
          .upsert({
            id: data.user.id,
            email: data.user.email,
            name: meta.name || "",
            type: desiredType,
          });
        if (!upsertError) {
          const res = await supabase
            .from("profiles")
            .select("type")
            .eq("id", data.user.id)
            .single();
          finalProfile = res.data;
          finalError = res.error;
        }
      }

      if (finalError || !finalProfile) {
        message.error("No user profile found.");
        await supabase.auth.signOut();
        return;
      }

      const userType = profileData.type;

      if (userType !== role) {
        const actual = ROLE_LABEL[userType] || "Unknown";
        message.error(
          `This account is registered as ${actual}. Please use the ${actual} login.`
        );
        await supabase.auth.signOut();
        return;
      }

      message.success(`${roleLabel} Login successful!`);
      navigate(LOGIN_REDIRECT[userType]);
    } catch (error) {
      console.error("Error during login:", error);
      message.error("Invalid email or password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-background to-muted">
      <div className="flex flex-col lg:flex-row w-full max-w-7xl bg-white shadow-lg overflow-hidden">
        <div className="w-full lg:w-1/2 flex items-center justify-center bg-gray-200">
          <div className="w-full max-w-lg">
            <Card className="shadow-soft rounded-2xl bg-white animate-scale-in" style={{ borderRadius: "16px" }}>
              <Title level={2} className="text-center text-gray-800 text-xl md:text-2xl">
                {roleLabel} Login
              </Title>
              <Divider />
              <Text type="none" className="block text-center text-foreground text-sm md:text-base">
                Welcome back! Sign in to access your {roleLabel} dashboard.
              </Text>

              <Form className="py-4 bg-gray-100 px-2 rounded-lg" layout="vertical" onFinish={handleLogin}>
                <Form.Item label="Email" name="email" rules={[{ required: true, message: "Please enter your email!" }]}>
                  <Input prefix={<MailOutlined className="text-gray-400" />} placeholder="Enter your email" className="rounded-lg py-2" />
                </Form.Item>

                <Form.Item label="Password" name="password" rules={[{ required: true, message: "Please enter your password!" }]}>
                  <Input.Password prefix={<LockOutlined className="text-gray-400" />} placeholder="Enter your password" className="rounded-lg py-2" />
                </Form.Item>

                <Form.Item>
                  <Button
                    type="none"
                    icon={<LoginOutlined />}
                    className="bg-primary py-5 text-white font-semibold hover:opacity-90 rounded-lg"
                    htmlType="submit"
                    loading={loading}
                    block
                  >
                    Login
                  </Button>
                </Form.Item>
              </Form>

              {role !== "admin" && (
                <div className="text-center">
                  <Text type="secondary" className="text-sm">
                    Don't have an account?{" "}
                    <Link to={`/signup/${role}`} className="text-primary font-semibold">
                      Sign up as {roleLabel}
                    </Link>
                  </Text>
                </div>
              )}
            </Card>
          </div>
        </div>

        <div className="w-full lg:w-1/2 relative hidden lg:block overflow-hidden">
          <div className="absolute bottom-0 left-0 w-full h-full bg-gradient-to-t from-primary/85 to-transparent flex items-end justify-center z-10 p-6">
            <div className="text-center text-white pb-[50px]">
              <h2 className="text-2xl font-bold mb-2 md:text-2xl">Learning Management System</h2>
              <p className="text-sm md:text-lg">
                This system enables seamless management of educational content, student information, and resources.
              </p>
            </div>
          </div>
          <img src={loginImage} alt="Learning Management System" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
        </div>
      </div>
    </div>
  );
}
